import { useEffect, useRef, useState } from "react";
import { Play, Pause, Upload, Loader2, Music2 } from "lucide-react";
import { api, fileUrl } from "./api";
import { useAuth } from "./AuthContext";

export function MixesPage() {
  const { isOrganizer } = useAuth();
  const [mixes, setMixes] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const load = () => api.get("/mixes").then((r) => setMixes(r.data)).catch(() => setMixes([]));
  useEffect(() => { load(); }, []);

  const toggle = (mix) => {
    if (playing === mix.id) { audioRef.current?.pause(); setPlaying(null); return; }
    setPlaying(mix.id);
    setTimeout(() => { if (audioRef.current) { audioRef.current.src = fileUrl(mix.storage_path); audioRef.current.play().catch(() => {}); } }, 50);
  };

  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">/ Listen</p>
      <h1 className="mt-3 mb-8 font-oswald text-5xl font-700 uppercase leading-[0.85] tracking-tighter">Mixes</h1>

      {isOrganizer && <UploadMix onDone={load} />}

      <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />
      {mixes === null && <p className="text-zinc-500">Loading…</p>}
      {mixes?.length === 0 && <p className="text-zinc-500">No mixes uploaded yet.</p>}
      <div className="mt-4 grid gap-3">
        {mixes?.map((m) => (
          <div key={m.id} data-testid={`mix-${m.id}`} className="flex items-center gap-4 border border-white/10 bg-ink-900/60 p-4">
            <button data-testid={`play-${m.id}`} onClick={() => toggle(m)}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold text-ink-950">
              {playing === m.id ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <div className="min-w-0">
              <p className="truncate font-oswald text-xl font-600 uppercase tracking-tight">{m.title}</p>
              {m.genre && <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{m.genre}</span>}
            </div>
            <Music2 size={18} className={`ml-auto ${playing === m.id ? "text-gold animate-pulse" : "text-zinc-600"}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function UploadMix({ onDone }) {
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title) { setMsg("Title and audio file required"); return; }
    setBusy(true); setMsg("");
    const fd = new FormData();
    fd.append("title", title); fd.append("genre", genre); fd.append("file", file);
    try {
      await api.post("/mixes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setGenre(""); setFile(null); setMsg("Uploaded!"); onDone();
    } catch (e) { setMsg("Upload failed"); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} data-testid="upload-mix-form" className="mb-8 border border-gold/30 bg-ink-900/40 p-5">
      <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Drop a new mix</p>
      <div className="grid gap-3">
        <input data-testid="mix-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mix title"
          className="border border-white/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-gold" />
        <input data-testid="mix-genre" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="Genre (e.g. Afro · House)"
          className="border border-white/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-gold" />
        <input data-testid="mix-file" type="file" accept="audio/*" onChange={(e) => setFile(e.target.files[0])}
          className="text-sm text-zinc-400 file:mr-3 file:border file:border-white/20 file:bg-transparent file:px-3 file:py-2 file:text-gold" />
        <button data-testid="mix-submit" disabled={busy}
          className="flex items-center justify-center gap-2 bg-gold py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ink-950 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><Upload size={15} /> Upload Mix</>}
        </button>
        {msg && <p className="text-sm text-zinc-400">{msg}</p>}
      </div>
    </form>
  );
}
